import { gateLayout } from '../domain/layout.js';
import type { ReadOnlyGameModel } from "yage/game/GameModel";
import { Transform } from "yage/schemas/entity/Transform";
import { PlayerInput } from "yage/schemas/core/PlayerInput";
import { AirportAgent } from "../components/index.js";
import { ids, state, zoneAvailable } from "./world.js";
import { walkableAirportPoint } from '../domain/config.js';
import { tier } from '../domain/airports.js';
import { objectiveAction, guidanceTarget } from './guidance.js';
import type { ProfileStore } from "../puzzles/profile.js";
import type { Point } from '../domain/types.js';
export const LOCAL: {
    model: ReadOnlyGameModel | null;
    slot: number;
    profiles: ProfileStore | null;
    muted: boolean;
    debugCollision: boolean;
    reducedMotion: boolean;
    /** This device's pace preference, applied to rooms it hosts. */
    relaxed: boolean;
    /** TX-015 controls: taps and drags (default), taps only, or drag stick only. */
    controls: 'hybrid' | 'tap' | 'stick';
    /** TX-020 guidance: full cues, or experienced (no chevrons or idle glow; rules identical). */
    guidance: 'full' | 'experienced';
    /** TX-023 Act II inbox on this device: important notices, newest last, and how many are unread. */
    inbox: string[];
    inboxUnread: number;
    saveStatus: string;
    modalOpen: boolean;
    modalPaused: boolean;
    onOpen: ((kind: string) => void) | null;
    epoch: number;
    guidanceFocus: { point: Point; captain: number; origin: Point } | null;
    /** A short presentation-only camera follow of one entity (the first takeoff); the captain moving ends it. */
    cinematic: { entity: number; until: number; captain: number; origin: Point } | null;
    focusedProgression: boolean;
    solo: boolean;
    onPause: ((paused: boolean) => void) | null;
} = { model: null, slot: 0, profiles: null, muted: false, debugCollision: false, reducedMotion: false, relaxed: false, controls: 'hybrid', guidance: 'full', inbox: [], inboxUnread: 0, saveStatus: 'Loading save…', modalOpen: false, modalPaused: false, onOpen: null, epoch: 0, guidanceFocus: null, cinematic: null, focusedProgression: true, solo: true, onPause: null };
export function setPaused(paused: boolean) {
    if (!LOCAL.solo || !LOCAL.model) return;
    LOCAL.model.paused = paused;
    LOCAL.onPause?.(paused);
}
export function localPlayers(g: ReadOnlyGameModel): number[] { return ids(g, AirportAgent).filter(id => g.localNetIds.includes(g.getTypedUnsafe(PlayerInput, id).pid)); }
export function localPlayer(g: ReadOnlyGameModel): number { return localPlayers(g)[LOCAL.slot % Math.max(1, localPlayers(g).length)] ?? -1; }
export function send(name: string, data: unknown = {}) {
    const g = LOCAL.model;
    if (!g || g.paused && !LOCAL.modalPaused) return;
    const id = localPlayer(g);
    if (id < 0) return;
    if (name === 'move') {
        const point = data as Point;
        if (!point || !walkableAirportPoint(point, tier(state(g)).maxX, gateLayout(tier(state(g)).code).OUTLINE)) return;
        LOCAL.guidanceFocus = null;
    }
    g.event(g.getTypedUnsafe(PlayerInput, id).pid, 'airport:' + name, data);
}
export function travel(zone: number) {
    const g = LOCAL.model;
    if (!g || zone < 0 || !zoneAvailable(g, zone)) return;
    const p = g.getTypedUnsafe(Transform, zone);
    send('move', { x: p.x, y: p.y, zone });
}

/** GO shows the destination; only the player's own movement starts work there. */
export function followObjective() {
    const g = LOCAL.model, pid = g ? localPlayer(g) : -1;
    if (!g || pid < 0) return;
    const action = objectiveAction(g, pid);
    LOCAL.guidanceFocus = null;
    if (action.view) { LOCAL.onOpen?.(action.view); return; }
    const point = guidanceTarget(g, pid, action);
    if (point) {
        const origin = g.getTypedUnsafe(Transform, pid);
        LOCAL.guidanceFocus = { point: { x: point.x, y: point.y }, captain: pid, origin: { x: origin.x, y: origin.y } };
    }
}
